import { css } from '@emotion/react';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import { KeyboardEvent, useCallback, useState } from 'react';
import { AutoFocusInside } from 'react-focus-lock';
import { BOUND_SIDE, DateRange, DateRangeFormat, DateTimeFormat, dateTimeFrom, isInRange } from 'shared';

import { DeviceType, useMaxDeviceWidth } from '../../hooks/useDeviceType';
import { ButtonGroup, DateInput } from '../forms/ui';
import { LinkButton } from '../LinkButton';
import { SasIcon } from '../SasIcon';
import { SasModal } from '../SasModal';
import { theme } from '../theme';

import { FlightCalender, FlightCalenderProps } from './FlightCalendar';

const styles = {
  wrapper: (margin: string) => css`
    min-width: 532px;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md};
    margin: ${margin} ${margin} 0;

    @media (max-width: ${theme.breakpoints.tablet}) {
      min-height: 100vh;
      min-width: unset;
      margin: 0 0 ${theme.spacing.md};
    }
  `,
  calendars: css`
    display: flex;
    gap: ${theme.spacing.md};

    @media (max-width: ${theme.breakpoints.tablet}) {
      flex-direction: column;
      flex: 1;
      align-items: center;
      padding: 0 ${theme.spacing.md};
    }
  `,
  footer: css`
    display: flex;
    gap: ${theme.spacing.xl};
    padding: ${theme.spacing.lg} ${theme.spacing.lg};
    bottom: 0;
    position: sticky;
    background: ${theme.colors.gray[0]};

    @media (max-width: ${theme.breakpoints.tablet}) {
      flex-direction: column;
      border-top: 1px ${theme.colors.border} solid;
      background: ${theme.colors.white};
      padding: ${theme.spacing.md};
      gap: ${theme.spacing.md};
      width: 100%;
      bottom: 0;
      left: 0;
    }
  `,
  actions: css`
    align-self: start;
    justify-content: flex-end;
    display: flex;
    gap: ${theme.spacing.xl};
    flex-grow: 1;

    @media (max-width: ${theme.breakpoints.tablet}) {
      align-self: flex-end;
    }
  `,
  mobileInputWrapper: css`
    background-color: ${theme.colors.white};
    display: flex;
    gap: ${theme.spacing.sm};
    padding: ${theme.spacing.md} ${theme.spacing.sm};
    border-bottom: 1px ${theme.colors.border} solid;
    position: sticky;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 1;

    > button {
      outline: none;
      background: transparent;
      margin: 0;
      padding: 0;
      min-height: auto;
      min-width: auto;
      border: 1px solid transparent;
      border-radius: 0;
      flex-shrink: 0;

      &:active,
      &:focus,
      &:focus-visible {
        outline: none;
        box-shadow: none;
        border: 1px solid ${theme.colors.border};
      }
    }

    > div {
      flex-grow: 1;
    }
  `,
  calendarBar: css`
    display: flex;
    gap: ${theme.spacing.xl};

    @media (max-width: ${theme.breakpoints.tablet}) {
      margin: 0 ${theme.spacing.md};
      flex-direction: column;
      gap: ${theme.spacing.md};
    }
  `,
  legends: css`
    display: flex;
    gap: ${theme.spacing.md};
  `,
  legend: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xs};
  `,
  legendIndicator: (color: string) => css`
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background-color: ${color};
    display: inline-block;
  `,
};

export type FlightBoundDates = { outboundDate: string | undefined; inboundDate: string | undefined };

type Legend = {
  color: string;
  label: string;
};

type Calendar = {
  Component: React.ComponentType<FlightCalenderProps>;
  label: string;
  legends?: Legend[];
  Footer?: React.ComponentType<{
    outboundDate: DateTime<boolean> | undefined;
    inboundDate: DateTime<boolean> | undefined;
  }>;
};

type FlightDateSelectorProps = {
  onSubmit: (date: FlightBoundDates) => void;
  handleCloseModal: () => void;
  outboundDate?: string;
  inboundDate?: string;
  selectable: DateRange;
  displayOutboundDatePicker?: boolean;
  displayInboundDatePicker?: boolean;
  calendars?: Calendar[];
  originalOutboundDate?: string;
  originalInboundDate?: string;
  buttonText?: string;
};

export function FlightDateSelector(props: FlightDateSelectorProps) {
  const isSmall = useMaxDeviceWidth(DeviceType.Tablet);
  const buttonText = props.buttonText ?? t('searchFlight');
  const [selectedCalendar, setSelectedCalendar] = useState(props.calendars?.[0]?.label);
  const [dates, setDates] = useState({
    outboundDate: props.outboundDate,
    inboundDate: props.inboundDate,
  });

  const originalOutboundDate = props.originalOutboundDate ?? props.outboundDate;
  const originalInboundDate = props.originalInboundDate ?? props.inboundDate;

  const handleReset = useCallback(() => {
    setDates({
      outboundDate: originalOutboundDate,
      inboundDate: originalInboundDate,
    });
  }, [originalOutboundDate, originalInboundDate]);

  const handleDateChange = (boundSide: BOUND_SIDE, date: DateTime) => {
    const newDates = updateBoundDates(dates, date, boundSide, props.selectable);

    if (newDates) {
      setDates(newDates);
    }
  };

  const handleSearch = () => {
    props.onSubmit(dates);
  };

  const outboundDate = dates.outboundDate ? DateTime.fromISO(dates.outboundDate) : undefined;
  const inboundDate = dates.inboundDate ? DateTime.fromISO(dates.inboundDate) : undefined;

  const { displayOutboundDatePicker = true, displayInboundDatePicker = true } = props;

  const calendar = props.calendars?.find(calendar => calendar.label === selectedCalendar);

  const Calendar = calendar?.Component ?? FlightCalender;
  const Footer = calendar?.Footer;

  const calenderRadioGroup = props.calendars && props.calendars.length > 1 && (
    <ButtonGroup
      options={props.calendars.map(calendar => ({ value: calendar.label, label: calendar.label }))}
      selected={selectedCalendar ?? props.calendars[0].label}
      onChange={setSelectedCalendar}
      fitContent={!isSmall}
    />
  );

  const calenderLegends = calendar?.legends && calendar.legends.length > 0 && (
    <span css={styles.legends}>
      {calendar.legends.map(legend => (
        <span key={legend.label} css={styles.legend}>
          <span css={styles.legendIndicator(legend.color)} />
          <span>= {legend.label}</span>
        </span>
      ))}
    </span>
  );

  const hasCalendarBar = calenderRadioGroup || calenderLegends;

  return (
    <SasModal
      open={true}
      handleClose={props.handleCloseModal}
      contentBg={theme.colors.gray[0]}
      showCloseButton={!isSmall}
      padding={0}
      fitContent
      hasCalendarBar={!!hasCalendarBar}
    >
      <div css={styles.wrapper(hasCalendarBar ? theme.spacing.xl : theme.spacing.xxl)}>
        {isSmall && (
          <div css={styles.mobileInputWrapper}>
            <button type="button" onClick={props.handleCloseModal}>
              <SasIcon name="in--arrow-left" />
            </button>
            {displayOutboundDatePicker && (
              <DateInput
                selected={outboundDate}
                title={t('common:from')}
                onSelect={date => handleDateChange(BOUND_SIDE.Outbound, date)}
                selectable={props.selectable}
              />
            )}
            {displayInboundDatePicker && (
              <DateInput
                selected={inboundDate}
                title={t('common:to')}
                onSelect={date => handleDateChange(BOUND_SIDE.Inbound, date)}
                selectable={props.selectable}
              />
            )}
          </div>
        )}

        {hasCalendarBar && (
          <div css={styles.calendarBar}>
            {calenderRadioGroup}
            {calenderLegends}
          </div>
        )}

        <AutoFocusInside css={styles.calendars}>
          <Calendar
            selectable={props.selectable}
            outboundDate={outboundDate}
            handleDateChange={handleDateChange}
            inboundDate={inboundDate}
            displayOutboundDatePicker={displayOutboundDatePicker}
            displayInboundDatePicker={displayInboundDatePicker}
          />
        </AutoFocusInside>
      </div>
      <div css={styles.footer}>
        {Footer && <Footer outboundDate={outboundDate} inboundDate={inboundDate} />}
        <div css={styles.actions}>
          <LinkButton
            onClick={handleReset}
            onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => e.key === 'Enter' && handleReset()}
          >
            {t('common:reset')}
          </LinkButton>

          <button
            style={{ height: '48px', minWidth: '190px' }}
            onClick={handleSearch}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </SasModal>
  );
}

type BoundDates = {
  outboundDate?: DateTimeFormat;
  inboundDate?: DateTimeFormat;
};

// Adjust the outbound to not be after the inbound and that the outbound is at most 359 days before the inbound
const restrictOutbound = (outboundDateTime: DateTime<true>, inboundDateTime: DateTime<true>) => {
  const earliestOutbound = inboundDateTime.minus({ days: 359 });
  const earliestSelected = DateTime.min(outboundDateTime, inboundDateTime);
  return DateTime.max(earliestOutbound, earliestSelected);
};

// Adjust the inbound to not be before the outbound and that the inbound is at most 359 days after the outbound
const restrictInbound = (outboundDateTime: DateTime<true>, inboundDateTime: DateTime<true>) => {
  const latestInbound = outboundDateTime.plus({ days: 359 });
  const latestSelected = DateTime.max(outboundDateTime, inboundDateTime);
  return DateTime.min(latestInbound, latestSelected);
};

export function updateBoundDates(
  currentDates: BoundDates,
  newDate: DateTimeFormat,
  changedBound: BOUND_SIDE,
  validBoundary: DateRangeFormat
): { outboundDate: string | undefined; inboundDate: string | undefined } | undefined {
  const outboundDateTime = currentDates.outboundDate
    ? dateTimeFrom(currentDates.outboundDate).startOf('day')
    : undefined;

  const inboundDateTime = currentDates.inboundDate ? dateTimeFrom(currentDates.inboundDate).startOf('day') : undefined;

  const newDateTime = dateTimeFrom(newDate);

  if (
    outboundDateTime?.isValid === false ||
    inboundDateTime?.isValid === false ||
    newDateTime.isValid === false ||
    !isInRange({ date: newDateTime, range: validBoundary })
  ) {
    return undefined;
  }

  return changedBound === BOUND_SIDE.Outbound
    ? {
        outboundDate: newDateTime.toISO(),
        inboundDate: inboundDateTime ? restrictInbound(newDateTime, inboundDateTime).toISO() : undefined,
      }
    : {
        outboundDate: outboundDateTime ? restrictOutbound(outboundDateTime, newDateTime).toISO() : undefined,
        inboundDate: newDateTime.toISO(),
      };
}
